<template>
	<div class="left-menu">
		<div class="left-search">
			<p class="txt">{{ facilityName }}</p>
			<button class="btn-search" @click="pop = true"></button>
		</div>
		<div class="left-info">
			<ul class="name-address">
				<li>
					<strong>{{ lang.label('facilityName2') }}</strong>
					<p>{{ facilityName }}</p>
				</li>
				<li>
					<strong style="padding-right: 40px; white-space: nowrap;">{{ lang.label('addr') }}</strong>
					<p>{{ facilityAddress }}</p>
				</li>
			</ul>
			<div class="img">
				<img src="../assets/img/img_place.png" alt="현장사진">
			</div>
			<div class="notice-slide">
				<strong>{{ lang.label('notice') }}</strong>
				<div class="slide-wrap-box">
					<Swiper :modules="modules" :pagination="pagination">
						<SwiperSlide v-for="(page, pageIndex) in slidePages" :key="pageIndex">
							<div class="slide-in">
								<ul>
									<li v-for="(item) in page" :key="item.id" @click="openPostPopup(item.id)">
										<p class="tit">{{ item.boardTitle ?? '' }}</p>
<!--										<span v-if="item.important" class="bul"></span>-->
										<span class="date">{{ formatDate(item.createdDatetime) ?? '' }}</span>
									</li>
								</ul>
							</div>
						</SwiperSlide>
					</Swiper>
				</div>
				<div class="notice-mob">
					<Swiper :modules="modules" :pagination="pagination" :slides-per-view="1" :space-between="10">
						<SwiperSlide v-for="(page, pageIndex) in mobileSlidePages" :key="pageIndex">
							<div class="slide-in">
								<ul>
									<li v-for="(item) in page" :key="item.id" @click="openPostPopup(item.id)">
										<p class="tit">{{ item.boardTitle ?? '' }}</p>
										<span class="date">{{ formatDate(item.createdDatetime) ?? '' }}</span>
									</li>
								</ul>
							</div>
						</SwiperSlide>
					</Swiper>
				</div>
			</div>
		</div>
		<!-- <div class="left-logout">
				<button class="btn-logout">로그아웃</button>
				<button class="btn-mini"></button>
		</div> -->
	</div>

	<!-- 공지사항 -->
	<div class="popup-wrap" v-if="pop2">
		<div class="popup" style="width:500px;" v-click-outside="handleOutsideClose">
			<div class="popup-top">
				<strong>{{ lang.label('notice') }}</strong>
				<button class="btn-close" @click="pop2 = false"></button>
			</div>
			<div class="popup-btm">
				<strong class="notice-preview-tit">{{ boardTitle }}</strong>
				<div class="notice-preview-cont" v-html="boardContentHtml"></div>
			</div>
			<div class="popup-btn">
				<v-btn class="custom-btn btn-blue" @click="pop2 = false">{{ lang.button('close') }}</v-btn>
			</div>
		</div>
	</div>

	<div class="popup-wrap" v-if="pop">
		<placeSearchPop @closePop="pop = false"></placeSearchPop>
	</div>
</template>

<script setup lang="ts">
import placeSearchPop from './placeSearchPop.vue'
import {useRoute} from 'nuxt/app';
import {Swiper, SwiperSlide} from 'swiper/vue';
import {ref, onMounted, computed, onUnmounted} from 'vue';
import {useFacility} from "~/composables/useFacility";
import {useHandleCode} from "~/composables/useHandleCode";
import {useLanguage} from "~/composables/useLanguage";
import 'swiper/css';
import 'swiper/css/pagination';
import {Pagination} from 'swiper/modules';
import {useBoardData} from '~/composables/useBoardData';
import type {BoardListRequest, BoardData} from "~/types/board";

const route = useRoute();
const pop = ref(false);
const pop2 = ref(false);

const modules = ref([Pagination])
const pagination = {
	clickable: true,
};

const {lang} = useLanguage();

// 공지사항 팝업 외부 클릭 시 닫기
const handleOutsideClose = () => {
	pop2.value = false;
};

const {
	boardArrayList,
	boardTitle,
	boardContentHtml,
	handlerBoardDetail,
	handlerEffectiveNoticeList
} = useBoardData();

// 게시글 슬라이드 페이지 수
const postsPerPage = 10;

// 게시글 슬라이드 분할
const slidePages = computed(() => {
	const pages: BoardData[][] = [];
	for (let i = 0; i < boardArrayList.value.length; i += postsPerPage) {
		pages.push(boardArrayList.value.slice(i, i + postsPerPage));
	}
	return pages;
})

// 모바일용 공지사항 리스트 (최대 3개)
const mobileNoticeList = computed(() => {
	return boardArrayList.value.slice(0, 3);
})

// 모바일용 슬라이드 페이지 (한 페이지당 3개씩, 최대 5페이지)
const mobileSlidePages = computed(() => {
	const pages: BoardData[][] = [];
	const itemsPerPage = 3;
	const maxPages = 5;
	const maxItems = maxPages * itemsPerPage; // 최대 15개 항목
	
	// 최대 항목 수만큼만 처리
	const limitedData = boardArrayList.value.slice(0, maxItems);
	
	for (let i = 0; i < limitedData.length; i += itemsPerPage) {
		pages.push(limitedData.slice(i, i + itemsPerPage));
	}
	return pages;
})

// 게시글 날짜 포맷팅
const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
	const date = new Date(dateStr);
  return date.toLocaleDateString('ko-KR')
      .replace(/\./g, '.')
      .replace(/\s/g, '')
      .replace(/\.$/, '');
};

// 게시글 클릭 시 상세보기
const openPostPopup = async (id: number) => {
	if (id === -1) return;
	await handlerBoardDetail(id);
	pop2.value = true;
};

const { handleErrorCode } = useHandleCode();

const {
	facility,
	facilityName,
	facilityAddress,
	setFacility,
	handleFacilityTableData,
	handleFacilityData
} = useFacility();

// 인터벌 ID
let intervalId: ReturnType<typeof setInterval> | null = null;

onMounted(async () => {
	await handleFacilityTableData();
	await handlerEffectiveNoticeList({
		page: 0,
		size: 10
	} as BoardListRequest);

	// 3) 쿼리에서 facilityId 읽기
	const facilityId = Number(route.query.facilityId)
	if (facilityId) {
		// facility 객체 조회 (필터링 파라미터 형태는 API 스펙에 맞춰 조정하세요)
		await handleFacilityData({ id: facilityId })

		if (!facility.value) {
			// 해당 ID로 조회된 데이터가 없을 때
			handleErrorCode('E10002')
		} else if (facility.value.terminals.length === 0) {
			// 터미널 데이터가 비어 있을 때
			handleErrorCode('E10006')
		} else {
			// 정상적으로 현장, 터미널 세팅;
			setFacility(facility.value)
		}
	}

	intervalId = setInterval(async () => {
		await handlerEffectiveNoticeList({
			page: 0,
			size: 10
		} as BoardListRequest);
	}, 1000 * 60 * 5); // 5분마다 호출
})

onUnmounted(() => {
	if (intervalId) clearInterval(intervalId);
});
</script>